import React from 'react';

import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {defineMessages, useIntl} from 'react-intl';
import {useQuery} from 'react-query';

import API, {Types} from '@wheeebo/polaris-api';
import {CircleLoader, LoadError} from '@wheeebo/polaris-ui';

const ITEM_COUNT = 10;

const msgs = defineMessages({
  title: {
    id: 'pages.admin.LoginEventsCard.title',
    defaultMessage: 'Login history',
  },
  subheader: {
    id: 'pages.admin.LoginEventsCard.subheader',
    defaultMessage: 'Recent login attempts (success and denial) on Polaris sites.',
  },
  cellTime: {
    id: 'pages.admin.LoginEventsCard.cell.time',
    defaultMessage: 'Time',
  },
  cellEmail: {
    id: 'pages.admin.LoginEventsCard.cell.email',
    defaultMessage: 'Email',
  },
  cellSite: {
    id: 'pages.admin.LoginEventsCard.cell.site',
    defaultMessage: 'Site',
  },
  cellMethod: {
    id: 'pages.admin.LoginEventsCard.cell.method',
    defaultMessage: 'Method',
  },
  cellResult: {
    id: 'pages.admin.LoginEventsCard.cell.result',
    defaultMessage: 'Result',
  },
  cellDetail: {
    id: 'pages.admin.LoginEventsCard.cell.detail',
    defaultMessage: 'Detail',
  },
});

function formatTime(ms: number): string {
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
}

function eventDetail(e: Types.LoginEvent): string {
  const parts: string[] = [];
  if (e.result !== 'succeeded' && e.reason) parts.push(e.reason);
  if (e.ip) parts.push(e.ip);
  return parts.join(' · ');
}

interface Props {
  memberId?: Types.UUID;
}

function LoginEventsCard({memberId}: Props): JSX.Element {
  const intl = useIntl();
  const [page, setPage] = React.useState(0);
  const options: API.Members.LoginEventsProps = {
    limit: ITEM_COUNT,
    offset: page * ITEM_COUNT,
    orderBy: 'created_at',
    order: 'desc',
    memberId,
  };
  const {data, isError, isLoading} = useQuery(API.Members.loginEventsKey(options), () =>
    API.Members.loginEvents(options),
  );
  return (
    <Card>
      <CardHeader title={intl.formatMessage(msgs.title)} subheader={intl.formatMessage(msgs.subheader)} />
      <CardContent>
        {isLoading ? <CircleLoader /> : null}
        {isError ? <LoadError /> : null}
        {data && data.total > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{intl.formatMessage(msgs.cellTime)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellEmail)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellSite)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellMethod)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellResult)}</TableCell>
                  <TableCell>{intl.formatMessage(msgs.cellDetail)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((e: Types.LoginEvent) => (
                  <TableRow key={e.id}>
                    <TableCell sx={{whiteSpace: 'nowrap'}}>{formatTime(e.createdAt)}</TableCell>
                    <TableCell>{e.email || '-'}</TableCell>
                    <TableCell>{e.site}</TableCell>
                    <TableCell>{e.method}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={e.result}
                        color={e.result === 'succeeded' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                      {eventDetail(e) || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[ITEM_COUNT]}
                    count={data.total || 0}
                    rowsPerPage={ITEM_COUNT}
                    page={page}
                    onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                      setPage(newPage)
                    }
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          !isLoading && <Typography variant="body2">No record found.</Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default React.memo(LoginEventsCard);
